import { ReportMetadata } from '../types';
import { getRepresentatives, istPlanungsVerantwortlicher, matchesRepresentative } from './projectRepresentatives';
import { isUserActiveSubstitute } from './substitutionService';

/**
 * Sichtbarkeit eines Projekts für einen Nutzer — eine Stelle statt vier
 * Kopien in Projektliste, Dashboard, Aufgaben, Karte und Urlaubsplaner.
 * Die Kriterien entsprechen der RLS-Funktion `public.can_access_project`
 * (supabase/rls_hardening.sql, erweitert in supabase/representatives_access.sql).
 *
 * Bewusst als Relations-Objekt und nicht als boolean: die Aufrufer
 * unterscheiden weiterhin Eigentümer, Vertreterliste und Urlaubsvertretung,
 * seit dem Projekt-Kickoff auch Planung und Ausführung (`isPlanningOnly`).
 * Wer in der Vertreterliste steht, ist zuständig — im `representatives`-Array
 * gibt es keinen Aktiv-Haken je Eintrag. Für Alt-Projekte ohne Array liefert
 * `getRepresentatives` nur noch die Legacy-Slots mit
 * `coordinatorDeputyActive`/`...2Active = true`; die deployte RLS-Fassung
 * (supabase/representatives_access.sql) prüft das NICHT und lässt einen
 * deaktivierten Alt-Stellvertreter noch an die Zeile — der Client ist hier
 * also strenger als die Datenbank, nie umgekehrt.
 */

export interface ProjectAccessUser {
    id?: string | null;
    email?: string | null;
}

/** Nur die Felder, die für die Sichtbarkeit zählen — passt auf jede Projektzeile. */
export interface ProjectAccessRow {
    user_id?: string | null;
    transfer_target_id?: string | null;
    data?: { metadata?: ReportMetadata | null } | null;
}

export interface ProjectRelation {
    /** Eigentümer laut DB-Spalte `projects.user_id`. */
    isOwner: boolean;
    /** Empfänger eines laufenden Projekt-Transfers (`projects.transfer_target_id`). */
    isTransferTarget: boolean;
    /** Hauptverantwortlicher laut Metadaten (`coordinatorId`). */
    isCoordinator: boolean;
    /** Aktive Urlaubsvertretung im laufenden Zeitfenster. */
    isSubstitute: boolean;
    /** Steht in der Vertreterliste — unabhängig von der Position. */
    isListed: boolean;
    /**
     * Steht auf Position 0 der Vertreterliste, also Hauptverantwortlicher —
     * auch bei Alt-Projekten ohne `coordinatorId`.
     */
    isPrimaryRepresentative: boolean;
    /** Steht in der Vertreterliste, aber hinter Position 0 (echte Vertretung). */
    isDeputyListed: boolean;
    /** Dauerhaft zuständig: Eigentümer, Koordinator oder in der Vertreterliste. */
    isResponsible: boolean;
    /**
     * Gelistet NUR als Verantwortliche Planungsphase (ab Position 1,
     * `bereich: 'planung'`): nicht Eigentümer, nicht `coordinatorId`, keine
     * aktive Urlaubsvertretung und kein weiterer Listeneintrag ohne 'planung'.
     */
    isPlanningOnly: boolean;
    /** Zuständig für die Ausführung (Begehungen): `isResponsible && !isPlanningOnly`. */
    isResponsibleAusfuehrung: boolean;
    /**
     * Wie `isResponsibleAusfuehrung`, aber ohne die Urlaubsvertretung: nur die
     * DAUERHAFTE Ausführungs-Verantwortung. Für den Urlaubsplaner — eine reine
     * Planungsperson, die gerade vertritt, darf das vertretene Projekt nicht
     * weiterdelegieren (keine Vertretungsketten, Codex-Review 12.10.).
     */
    isResponsibleAusfuehrungDauerhaft: boolean;
    /** Sammelurteil inkl. Urlaubsvertretung und offener Projektübernahme. */
    hasAccess: boolean;
}

const NO_RELATION: ProjectRelation = {
    isOwner: false,
    isTransferTarget: false,
    isCoordinator: false,
    isSubstitute: false,
    isListed: false,
    isPrimaryRepresentative: false,
    isDeputyListed: false,
    isResponsible: false,
    isPlanningOnly: false,
    isResponsibleAusfuehrung: false,
    isResponsibleAusfuehrungDauerhaft: false,
    hasAccess: false,
};

export const getProjectRelation = (
    project: ProjectAccessRow | undefined | null,
    user: ProjectAccessUser | undefined | null,
    date: Date = new Date()
): ProjectRelation => {
    if (!project || !user) return NO_RELATION;

    const userId = (user.id || '').trim();
    const email = (user.email || '').trim().toLowerCase();
    if (!userId && email.length <= 3) return NO_RELATION;

    const meta = (project.data?.metadata || undefined) as ReportMetadata | undefined;
    const reps = getRepresentatives(meta);

    const isOwner = !!userId && project.user_id === userId;
    const isTransferTarget = !!userId && project.transfer_target_id === userId;
    const isCoordinator = !!userId && meta?.coordinatorId === userId;
    const isSubstitute = isUserActiveSubstitute(meta, email, date);

    /*
     * Position statt nur Ja/Nein: Für die ZUGRIFFSPRÜFUNG bleibt `isListed`
     * unverändert (wer gelistet ist, kommt rein). Die ANZEIGE braucht aber die
     * Position — sonst landet der Hauptverantwortliche eines Alt-Projekts ohne
     * `coordinatorId` im Vertreter-Eimer und bekommt fremde Projekte als
     * „ich bin hier Vertreter" vorgesetzt.
     */
    const listedIndex = reps.findIndex(rep => matchesRepresentative(rep, user));
    const isListed = listedIndex >= 0;
    const isPrimaryRepresentative = listedIndex === 0;
    const isDeputyListed = listedIndex > 0;

    const isResponsible = isOwner || isCoordinator || isListed;

    /*
     * Planung oder Ausführung: Die Verantwortliche Planungsphase bleibt
     * zuständig und behält den Zugriff (`isResponsible`/`hasAccess` laufen
     * weiter gleich mit der RLS, deren Array-Zweig `bereich` nicht kennt).
     * Agenda, Aufgaben, Karte „Nur meine" und Urlaubsplaner drehen sich aber
     * um Begehungen — dort hat sie nichts verloren, sonst bekäme sie jede
     * fällige Begehung eines Projekts vorgesetzt, das sie nur plant.
     *
     * Alle passenden Einträge statt nur des ersten: `dedupe` führt eine
     * Person mit id auf dem einen und bloßer E-Mail auf dem anderen Eintrag
     * nicht zusammen, `matchesRepresentative` trifft aber beide. Steht sie
     * irgendwo ohne 'planung', ist sie (auch) Vertreter und damit nicht
     * „nur Planung". Position 0 ist immer Ausführung, gleich was dort steht.
     */
    const listedIndexes = reps
        .map((rep, idx) => (matchesRepresentative(rep, user) ? idx : -1))
        .filter(idx => idx >= 0);
    const nurPlanungGelistet =
        isListed
        && !isOwner
        && !isCoordinator
        && listedIndexes.every(idx => idx > 0 && istPlanungsVerantwortlicher(reps[idx]));
    const isPlanningOnly = nurPlanungGelistet && !isSubstitute;

    return {
        isOwner,
        isTransferTarget,
        isCoordinator,
        isSubstitute,
        isListed,
        isPrimaryRepresentative,
        isDeputyListed,
        isResponsible,
        isPlanningOnly,
        isResponsibleAusfuehrung: isResponsible && !isPlanningOnly,
        isResponsibleAusfuehrungDauerhaft: isResponsible && !nurPlanungGelistet,
        hasAccess: isResponsible || isTransferTarget || isSubstitute,
    };
};
